import React, { useEffect, useMemo, useRef, useState } from 'react';

import { format } from 'date-fns';
import moment from 'moment-timezone';
import DatePicker from 'react-datepicker';
import TimezoneSelect from 'react-timezone-select';

import 'react-datepicker/dist/react-datepicker.css';

import { buildMemberOptions } from 'Helpers/approvalRecipients';
import { timeStringToDate } from 'Helpers/dateUtils';
import { getDateFnsLocale } from 'Helpers/localeService';
import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import MultiSelectField from 'CommonComponents/MultiSelectField';
import SelectField from 'CommonComponents/SelectField';

import { useToast } from 'Hooks/useToast';

import XMarkIcon from 'Assets/icons/xmark.svg';

const WEEKDAYS = [
    { value: 'mon', label: $t('Mon') },
    { value: 'tue', label: $t('Tue') },
    { value: 'wed', label: $t('Wed') },
    { value: 'thu', label: $t('Thu') },
    { value: 'fri', label: $t('Fri') },
    { value: 'sat', label: $t('Sat') },
    { value: 'sun', label: $t('Sun') },
];

const MONTH_OPTIONS = [
    { value: 1, label: $t('January') },
    { value: 2, label: $t('February') },
    { value: 3, label: $t('March') },
    { value: 4, label: $t('April') },
    { value: 5, label: $t('May') },
    { value: 6, label: $t('June') },
    { value: 7, label: $t('July') },
    { value: 8, label: $t('August') },
    { value: 9, label: $t('September') },
    { value: 10, label: $t('October') },
    { value: 11, label: $t('November') },
    { value: 12, label: $t('December') },
];

const SPECIFIC_RULES = [
    { value: 'first_of_month', label: $t('First day of the month') },
    { value: 'last_day', label: $t('Last day of the month') },
    { value: 'last_friday', label: $t('Last Friday of the month') },
    { value: 'fifteenth_and_last', label: $t('15th and last day of the month') },
];

// The frequency picker's high-level modes map onto the backend's
// frequency_type + frequency_config shapes.
const FREQUENCY_OPTIONS = [
    { value: 'daily', label: $t('Daily') },
    { value: 'weekly', label: $t('Weekly') },
    { value: 'fortnightly', label: $t('Fortnightly') },
    { value: 'monthly', label: $t('Monthly') },
    { value: 'quarterly', label: $t('Quarterly') },
    { value: 'specific', label: $t('Specific dates') },
    { value: 'cron', label: $t('Custom (advanced)') },
];

// A pragmatic email-shape check for the recipient chip input — mirrors the
// server's `email` rule closely enough to catch obvious typos before submit.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// react-select style overrides that pin the timezone picker to the same 32px
// height as the text inputs and the other select fields, and lift its menu
// above the modal (z-60).
const SELECT_HEIGHT = 32;
const timezoneSelectStyles = {
    control: (base) => ({ ...base, minHeight: SELECT_HEIGHT, height: SELECT_HEIGHT }),
    valueContainer: (base) => ({ ...base, height: SELECT_HEIGHT, padding: '0 6px' }),
    input: (base) => ({ ...base, margin: '0px' }),
    indicatorsContainer: (base) => ({ ...base, height: SELECT_HEIGHT }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

/**
 * Derive the initial picker state from an existing schedule (edit) or defaults
 * (create). The picker's `frequency` is the UI mode; presets collapse the four
 * preset kinds onto their `preset` value, specific/cron map straight across.
 */
function initialFrequencyState(schedule, defaultTimezone) {
    const config = schedule?.frequency_config || {};
    const today = format(new Date(), 'yyyy-MM-dd');

    if (schedule?.frequency_type === 'cron') {
        return {
            frequency: 'cron',
            time: '09:00',
            days: [],
            dayOfMonth: 1,
            months: [1, 4, 7, 10],
            anchorDate: today,
            expression: config.expression || '',
            timezone: schedule.timezone || defaultTimezone,
        };
    }

    if (schedule?.frequency_type === 'specific') {
        return {
            frequency: 'specific',
            time: config.time || '09:00',
            days: [],
            dayOfMonth: 1,
            months: [1, 4, 7, 10],
            anchorDate: today,
            rule: config.rule || 'first_of_month',
            expression: '',
            timezone: schedule.timezone || defaultTimezone,
        };
    }

    if (schedule?.frequency_type === 'preset') {
        return {
            frequency: config.preset || 'daily',
            time: config.time || '09:00',
            days: config.days || ['mon'],
            dayOfMonth: config.day_of_month || 1,
            months: config.months || [1, 4, 7, 10],
            anchorDate: config.anchor_date || today,
            rule: 'first_of_month',
            expression: '',
            timezone: schedule.timezone || defaultTimezone,
        };
    }

    return {
        frequency: 'daily',
        time: '09:00',
        days: ['mon'],
        dayOfMonth: 1,
        months: [1, 4, 7, 10],
        anchorDate: today,
        rule: 'first_of_month',
        expression: '',
        timezone: defaultTimezone,
    };
}

/**
 * Assemble the backend { frequency_type, frequency_config } pair from the
 * picker's UI state.
 */
function buildFrequencyPayload(state) {
    if (state.frequency === 'cron') {
        return { frequency_type: 'cron', frequency_config: { expression: state.expression } };
    }

    if (state.frequency === 'specific') {
        return { frequency_type: 'specific', frequency_config: { rule: state.rule, time: state.time } };
    }

    if (state.frequency === 'weekly') {
        return { frequency_type: 'preset', frequency_config: { preset: 'weekly', days: state.days, time: state.time } };
    }

    if (state.frequency === 'fortnightly') {
        return { frequency_type: 'preset', frequency_config: { preset: 'fortnightly', anchor_date: state.anchorDate, time: state.time } };
    }

    if (state.frequency === 'monthly') {
        return { frequency_type: 'preset', frequency_config: { preset: 'monthly', day_of_month: Number(state.dayOfMonth), time: state.time } };
    }

    if (state.frequency === 'quarterly') {
        return {
            frequency_type: 'preset',
            frequency_config: { preset: 'quarterly', months: state.months, day_of_month: Number(state.dayOfMonth), time: state.time },
        };
    }

    return { frequency_type: 'preset', frequency_config: { preset: 'daily', time: state.time } };
}

// Match the Generated / Scheduled Reports list convention so every user-facing
// date/time in the Report Centre reads the same way, rather than the raw,
// locale-dependent output of toLocaleString(). Unlike those list columns, the
// next-run preview is rendered in the schedule's own configured timezone (not
// the viewer's browser timezone) so it matches the "…at 09:00 <tz>" cadence
// the user just picked. `moment-timezone` (already a dependency) resolves the
// wall-clock time in that zone; re-parsing it as a bare local string (no
// offset) lets date-fns' `format()` read those wall-clock digits back via its
// normal local-time getters, without pulling in `date-fns-tz`.
const formatDateTime = (value, timezone) => {
    if (!value) {
        return '-';
    }
    try {
        const zoned = timezone ? new Date(moment.utc(value).tz(timezone).format('YYYY-MM-DDTHH:mm:ss')) : new Date(value);
        return format(zoned, 'd MMM yyyy, HH:mm', { locale: getDateFnsLocale() });
    } catch (error) {
        return value;
    }
};

/**
 * Create or edit a scheduled report. The report definition (name / parameters /
 * supported formats) is fixed; the modal captures the schedule's name, cadence,
 * timezone, output formats and recipients. As the cadence changes it debounces a
 * preview request to show a plain-English description and the next few runs. On
 * create the ad-hoc `parameters` snapshot is sent; on edit the schedule's own
 * parameters travel with it.
 */
export default function ScheduleReportModal({
    modal,
    mode = 'create',
    nonce = 0,
    report,
    schedule = null,
    parameters = {},
    defaultTimezone = 'UTC',
    storeUrl,
    updateUrl,
    previewUrl,
    onSaved,
    // Personalised delivery (IB-673): when the report supports it, expose a
    // toggle that generates a separate, per-recipient-scoped report for each
    // InBudget recipient. Personalised sends are InBudget-users-only, so the
    // free-text email input is swapped for a member picker sourced from these
    // options ({ name, email }). Both default off/empty, so callers that omit
    // them (the generic catalogue) keep the original shared-send behaviour.
    personalisable = false,
    userOptions = [],
}) {
    const toast = useToast();
    const isEdit = mode === 'edit';

    const supportedFormats = useMemo(() => report?.supportedFormats || ['xlsx'], [report]);
    const memberOptions = useMemo(() => buildMemberOptions(userOptions), [userOptions]);
    const memberEmails = useMemo(() => new Set(memberOptions.map((option) => option.value)), [memberOptions]);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [freq, setFreq] = useState(() => initialFrequencyState(schedule, defaultTimezone));
    const [formats, setFormats] = useState(supportedFormats);
    const [recipients, setRecipients] = useState([]);
    const [recipientDraft, setRecipientDraft] = useState('');
    const [personalised, setPersonalised] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const [preview, setPreview] = useState({ human: '', nextRuns: [], errors: [] });
    const [previewLoading, setPreviewLoading] = useState(false);

    // Reset the whole form on every open (nonce bumps per open) so a create form
    // never keeps stale input and an edit always reflects the row being edited.
    useEffect(() => {
        setName(schedule?.name || '');
        setDescription(schedule?.description || '');
        setFreq(initialFrequencyState(schedule, defaultTimezone));
        setFormats(schedule?.output_formats || supportedFormats);
        // Editing shows the schedule's non-owner recipients (the owner is locked
        // and always re-added server-side); creating starts empty.
        setRecipients((schedule?.recipients || []).filter((recipient) => !recipient.is_owner).map((recipient) => recipient.email));
        setRecipientDraft('');
        setPersonalised(personalisable && !!schedule?.personalised);
        setErrors({});
        setPreview({ human: '', nextRuns: [], errors: [] });
    }, [nonce]);

    // Personalised sends are InBudget-users-only: switching it on drops any
    // free-text external emails added while in shared mode, mirroring the
    // backend's per-recipient-user generation.
    const togglePersonalised = (next) => {
        setPersonalised(next);
        if (next) {
            setRecipients((prev) => prev.filter((email) => memberEmails.has(email)));
        }
    };

    // A personalised schedule swaps the free-text chip input for a member-only
    // picker; only when the report is personalisable AND the toggle is on.
    const usesMemberPicker = personalisable && personalised;

    const frequencyPayload = useMemo(() => buildFrequencyPayload(freq), [freq]);

    // Debounced preview: re-run whenever the cadence or timezone changes.
    const previewTimer = useRef(null);
    useEffect(() => {
        if (!previewUrl) {
            return undefined;
        }

        if (previewTimer.current) {
            clearTimeout(previewTimer.current);
        }

        previewTimer.current = setTimeout(() => {
            setPreviewLoading(true);
            apiClient
                .post(previewUrl, { ...frequencyPayload, timezone: freq.timezone })
                .then((response) => {
                    const data = response.data || {};
                    setPreview({ human: data.human_readable || '', nextRuns: data.next_runs || [], errors: data.errors || [] });
                })
                .catch(() => setPreview({ human: '', nextRuns: [], errors: [] }))
                .finally(() => setPreviewLoading(false));
        }, 400);

        return () => previewTimer.current && clearTimeout(previewTimer.current);
    }, [frequencyPayload, freq.timezone, previewUrl]);

    const setFreqField = (key, value) => setFreq((prev) => ({ ...prev, [key]: value }));

    const toggleDay = (day) => {
        setFreq((prev) => ({ ...prev, days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day] }));
    };

    const toggleFormat = (formatKey) => {
        setFormats((prev) => (prev.includes(formatKey) ? prev.filter((f) => f !== formatKey) : [...prev, formatKey]));
    };

    const addRecipient = () => {
        const email = recipientDraft.trim().replace(/,$/, '').trim();
        if (!email) {
            setRecipientDraft('');
            return;
        }
        // Reject a malformed address inline rather than letting it 422 on submit
        // with a recipients.N error the modal does not surface per-chip.
        if (!EMAIL_PATTERN.test(email)) {
            setErrors((prev) => ({ ...prev, recipients: $t('Enter a valid email address.') }));
            return;
        }
        if (!recipients.includes(email)) {
            setRecipients((prev) => [...prev, email]);
        }
        setErrors((prev) => ({ ...prev, recipients: undefined }));
        setRecipientDraft('');
    };

    const removeRecipient = (email) => setRecipients((prev) => prev.filter((item) => item !== email));

    const handleRecipientKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            addRecipient();
        }
    };

    const close = () => modal.hide();

    const submit = () => {
        if (!report) {
            return;
        }

        const nextErrors = {};
        if (!name.trim()) {
            nextErrors.name = $t('This field is required');
        }
        if (!formats.length) {
            nextErrors.output_formats = $t('Select at least one output format.');
        }
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) {
            return;
        }

        const payload = {
            report_key: report.type,
            name: name.trim(),
            description: description || null,
            parameters: schedule ? schedule.parameters || {} : parameters || {},
            source_filter_set_id: schedule?.source_filter_set_id || null,
            ...frequencyPayload,
            timezone: freq.timezone,
            output_formats: formats,
            // When this modal is personalisation-aware (the ProjectPlan builder),
            // send the toggle state. Otherwise preserve the schedule's existing
            // flag so a generic edit (the Scheduled Reports list, which passes no
            // personalisable/userOptions) never silently downgrades a personalised
            // schedule to a shared one.
            personalised: personalisable ? usesMemberPicker : !!schedule?.personalised,
            recipients,
        };

        setSubmitting(true);

        const promise = isEdit ? apiClient.put(updateUrl.replace('REPORT_SCHEDULE_ID', schedule.id), payload) : apiClient.post(storeUrl, payload);

        promise
            .then((response) => {
                const body = response.data || {};
                (body.warnings || []).forEach((warning) => toast.warning?.(warning) || toast.info?.(warning));
                toast.success(isEdit ? $t('Schedule updated.') : $t('Schedule created.'));
                onSaved?.(body.data || body, mode);
                close();
            })
            .catch((error) => {
                const serverErrors = error.response?.data?.errors;
                if (serverErrors) {
                    setErrors(Object.fromEntries(Object.entries(serverErrors).map(([field, messages]) => [field, [].concat(messages)[0]])));
                }
                toast.error(error.response?.data?.message || $t('Failed to save the schedule. Please try again.'));
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal modal={modal} modalIndexClass="tw-z-60">
            <ModalHeader title={isEdit ? $t('Edit schedule') : $t('Schedule report')} close={close} />

            <div className="modal__form-container tw-space-y-4">
                {report ? <div className="tw-text-sm tw-text-charcoal-75">{$t('Scheduling: {{name}}', { name: report.name })}</div> : null}

                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label htmlFor="schedule-name" className="text-input__label">
                        {$t('Name')}
                        <span className="tw-text-red tw-text-sm tw-ms-1">*</span>
                    </label>
                    <input
                        id="schedule-name"
                        type="text"
                        className="text-input tw-w-full"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder={$t('Name')}
                    />
                    {errors.name && <div className="tw-text-red tw-text-sm">{errors.name}</div>}
                </div>

                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label htmlFor="schedule-description" className="text-input__label">
                        {$t('Description')}
                    </label>
                    <textarea
                        id="schedule-description"
                        rows={2}
                        className="text-input tw-w-full"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder={$t('Description')}
                    />
                </div>

                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Frequency')}</label>
                        <SelectField
                            value={freq.frequency}
                            options={FREQUENCY_OPTIONS}
                            height={SELECT_HEIGHT}
                            onChange={(option) => setFreqField('frequency', option ? option.value : 'daily')}
                        />
                    </div>

                    {freq.frequency !== 'cron' && (
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label htmlFor="schedule-time" className="text-input__label">
                                {$t('Time')}
                            </label>
                            <DatePicker
                                id="schedule-time"
                                selected={timeStringToDate(freq.time)}
                                onChange={(date) => date && setFreqField('time', format(date, 'HH:mm'))}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={5}
                                timeCaption={$t('Time')}
                                dateFormat="HH:mm"
                                className="text-input tw-w-full"
                                autoComplete="off"
                            />
                        </div>
                    )}
                </div>

                {freq.frequency === 'weekly' && (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Days of the week')}</label>
                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                            {WEEKDAYS.map((day) => (
                                <button
                                    key={day.value}
                                    type="button"
                                    onClick={() => toggleDay(day.value)}
                                    className={`tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-grey ${
                                        freq.days.includes(day.value) ? 'tw-bg-blue tw-text-white' : 'tw-bg-white tw-text-charcoal hover:tw-bg-charcoal-10'
                                    }`}
                                >
                                    {day.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {freq.frequency === 'fortnightly' && (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label htmlFor="schedule-anchor-date" className="text-input__label">
                            {$t('Starting from')}
                        </label>
                        <DatePicker
                            id="schedule-anchor-date"
                            selected={freq.anchorDate ? new Date(freq.anchorDate) : null}
                            onChange={(date) => date && setFreqField('anchorDate', format(date, 'yyyy-MM-dd'))}
                            dateFormat="yyyy-MM-dd"
                            className="text-input tw-w-full"
                            autoComplete="off"
                        />
                        <p className="tw-text-xs tw-text-charcoal-75 tw-m-0">{$t('The report is sent every 14 days from this date.')}</p>
                    </div>
                )}

                {(freq.frequency === 'monthly' || freq.frequency === 'quarterly') && (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label htmlFor="schedule-dom" className="text-input__label">
                            {$t('Day of the month')}
                        </label>
                        <input
                            id="schedule-dom"
                            type="number"
                            min={1}
                            max={31}
                            className="text-input tw-w-32"
                            value={freq.dayOfMonth}
                            onChange={(event) => setFreqField('dayOfMonth', event.target.value)}
                        />
                    </div>
                )}

                {freq.frequency === 'quarterly' && (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Months')}</label>
                        <MultiSelectField
                            value={freq.months}
                            options={MONTH_OPTIONS}
                            clearable
                            onChange={(selected) =>
                                setFreqField(
                                    'months',
                                    (selected || []).map((option) => option.value),
                                )
                            }
                            placeholder={$t('Months')}
                        />
                    </div>
                )}

                {freq.frequency === 'specific' && (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="text-input__label">{$t('Rule')}</label>
                        <SelectField
                            value={freq.rule}
                            options={SPECIFIC_RULES}
                            height={SELECT_HEIGHT}
                            onChange={(option) => setFreqField('rule', option ? option.value : 'first_of_month')}
                        />
                    </div>
                )}

                {freq.frequency === 'cron' && (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label htmlFor="schedule-cron" className="text-input__label">
                            {$t('Cron expression')}
                        </label>
                        <input
                            id="schedule-cron"
                            type="text"
                            className="text-input tw-w-full"
                            value={freq.expression}
                            onChange={(event) => setFreqField('expression', event.target.value)}
                            placeholder="0 9 * * 1,4"
                        />
                    </div>
                )}

                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label className="text-input__label">{$t('Timezone')}</label>
                    <TimezoneSelect
                        value={freq.timezone}
                        isSearchable
                        classNamePrefix="react-select"
                        styles={timezoneSelectStyles}
                        menuPortalTarget={document.body}
                        placeholder={$t('Timezone')}
                        onChange={(timezone) => setFreqField('timezone', timezone?.value || defaultTimezone)}
                    />
                </div>

                {/* Live cadence preview. */}
                <div className="tw-p-3 tw-rounded-lg tw-bg-charcoal-10 tw-space-y-1.5">
                    <div className="tw-text-sm tw-font-semibold tw-text-charcoal">{$t('Schedule preview')}</div>
                    {previewLoading ? (
                        <div className="tw-text-sm tw-text-charcoal-75">{$t('Calculating…')}</div>
                    ) : (
                        <>
                            {preview.human && <div className="tw-text-sm tw-text-charcoal">{preview.human}</div>}
                            {preview.errors.length > 0 && <div className="tw-text-sm tw-text-red">{preview.errors.join(' ')}</div>}
                            {preview.nextRuns.length > 0 && (
                                <ul className="tw-text-xs tw-text-charcoal-75 tw-space-y-0.5">
                                    {preview.nextRuns.slice(0, 3).map((run) => (
                                        <li key={run}>{formatDateTime(run, freq.timezone)}</li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>

                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label className="text-input__label">{$t('Output formats')}</label>
                    <div className="tw-flex tw-flex-wrap tw-gap-3">
                        {supportedFormats.map((formatKey) => (
                            <label key={formatKey} className="tw-flex tw-items-center tw-gap-1.5 tw-cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="input-checkbox"
                                    checked={formats.includes(formatKey)}
                                    onChange={() => toggleFormat(formatKey)}
                                />
                                <span className="tw-text-sm tw-text-charcoal">{formatKey.toUpperCase()}</span>
                            </label>
                        ))}
                    </div>
                    {errors.output_formats && <div className="tw-text-red tw-text-sm">{errors.output_formats}</div>}
                </div>

                {personalisable && (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <div className="tw-flex tw-items-center tw-space-x-1.5">
                            <input
                                id="schedule-personalised"
                                type="checkbox"
                                className="input-checkbox"
                                checked={personalised}
                                onChange={(event) => togglePersonalised(event.target.checked)}
                            />
                            <label htmlFor="schedule-personalised" className="input-checkbox__label">
                                {$t('Personalise for each recipient')}
                            </label>
                        </div>
                        <p className="tw-text-xs tw-text-charcoal-75 tw-m-0">
                            {$t(
                                'Each InBudget recipient gets their own report, scoped to what they can see. Personalised reports cannot be sent to external email addresses.',
                            )}
                        </p>
                    </div>
                )}

                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label htmlFor="schedule-recipient" className="text-input__label">
                        {$t('Recipients')}
                    </label>
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-1.5">
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-bg-charcoal-15 tw-text-charcoal-75 tw-text-2xs tw-font-medium tw-py-1 tw-px-2">
                            {$t('You (always included)')}
                        </span>
                        {!usesMemberPicker &&
                            recipients.map((email) => (
                                <span
                                    key={email}
                                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-bg-blue-10 tw-text-blue-600 tw-text-2xs tw-font-medium tw-py-1 tw-px-2"
                                >
                                    {email}
                                    <button type="button" onClick={() => removeRecipient(email)} title={$t('Remove')} aria-label={$t('Remove')}>
                                        <XMarkIcon className="tw-w-3 tw-h-3" />
                                    </button>
                                </span>
                            ))}
                    </div>
                    {usesMemberPicker ? (
                        <MultiSelectField
                            value={recipients}
                            options={memberOptions}
                            clearable
                            onChange={(selected) => setRecipients((selected || []).map((option) => option.value))}
                            placeholder={$t('Select InBudget members')}
                        />
                    ) : (
                        <div className="tw-flex tw-gap-1.5">
                            <input
                                id="schedule-recipient"
                                type="email"
                                className="text-input tw-flex-1"
                                value={recipientDraft}
                                onChange={(event) => setRecipientDraft(event.target.value)}
                                onKeyDown={handleRecipientKeyDown}
                                onBlur={addRecipient}
                                placeholder={$t('Add an email address')}
                            />
                            <Button type="button" variant="secondary" onClick={addRecipient}>
                                {$t('Add')}
                            </Button>
                        </div>
                    )}
                    {errors.recipients && <div className="tw-text-red tw-text-sm">{errors.recipients}</div>}
                </div>

                <ModalFooter
                    submitButtonVariant="primary"
                    cancelButtonText={$t('Cancel')}
                    primaryButtonText={isEdit ? $t('Save changes') : $t('Create schedule')}
                    onCancel={close}
                    handleSubmit={submit}
                    isSubmitting={submitting}
                />
            </div>
        </Modal>
    );
}
